import { RootState } from '@iwProvider/configureStore';
import { format } from 'date-fns';
import { FormattedMessage, FormattedDate } from 'react-intl';
import { connect } from 'react-redux';

import styles from './ConfirmFundingInfo.module.css';
import {
    getRBLFundingFee,
    getEstimatedTotalRepaymentAmount,
    getEstimatedRepayments
} from '../../../api/lendingAPI/rblCalculatorSelector';
import {
    getDrawdownAmount,
    getRBLDrawdownSweep
} from '../../drawDown/offerConfigurationSelectors';
import { MoneyWithDecimals } from '../../ui/moneyWithDecimals/MoneyWithDecimals';
import moneyStyles from '../../ui/moneyWithDecimals/MoneyWithDecimals.module.css';
import PairItemContainer from '../../ui/pairItemContainer/PairItemContainer';
import { VariableRate } from '../../ui/rate/Rate';

const mapStateToProps = (state: RootState) => {
    const estimatedRepayments = getEstimatedRepayments(state);
    return {
        drawdownAmount: getDrawdownAmount(state),
        feeAmount: getRBLFundingFee(state),
        totalRBLAmount: getEstimatedTotalRepaymentAmount(state),
        sweepFraction: getRBLDrawdownSweep(state),
        firstRepaymentDate: estimatedRepayments?.[0]?.estimated_sweep_date,
        secondRepaymentDate: estimatedRepayments?.[1]?.estimated_sweep_date
    };
};

type PropsFromRedux = ReturnType<typeof mapStateToProps>;

const ConfirmFundingInfo = ({
    drawdownAmount,
    feeAmount,
    totalRBLAmount,
    sweepFraction,
    firstRepaymentDate,
    secondRepaymentDate,
    amount,
    sweep
}: { amount: number; sweep: number } & PropsFromRedux) => {
    return (
        <PairItemContainer>
            <div className={styles.drawdownSummary}>
                <div className={styles.summaryTitle}>
                    <FormattedMessage
                        id="_.rbl_drawdown.confirm.withdraw"
                        defaultMessage="You are requesting to withdraw"
                    />
                </div>

                <div className={styles.selectedValue}>
                    <MoneyWithDecimals
                        className={moneyStyles.MoneyWithDecimals}
                        value={amount || drawdownAmount}
                        decimalWithSeparatorClassName={
                            styles.repayMoneyDecimals
                        }
                    />
                </div>

                <div data-testid="rbl-drawdown-fee">
                    <FormattedMessage
                        id="_.rbl_drawdown.confirm.fee"
                        defaultMessage="On top of that, your loan has a fixed fee of {feeAmount}. We'll add this to your total repayment"
                        values={{
                            feeAmount: (
                                <MoneyWithDecimals
                                    className={styles.highlightedDrawdownValue}
                                    value={feeAmount}
                                />
                            )
                        }}
                    />
                </div>
            </div>

            <div className={styles.repaymentsSummary}>
                <div className={styles.summaryTitle}>
                    <FormattedMessage
                        id="_.rbl_drawdown.confirm.repayment_amount"
                        defaultMessage="Overall, you'll repay"
                    />
                </div>

                <div className={styles.selectedValue}>
                    {totalRBLAmount && (
                        <MoneyWithDecimals
                            className={moneyStyles.MoneyWithDecimals}
                            value={totalRBLAmount}
                            decimalWithSeparatorClassName={
                                styles.repayMoneyDecimals
                            }
                        />
                    )}
                </div>

                <div
                    className={styles.summaryTitle}
                    data-testid="rbl-drawdown-sweep"
                >
                    <FormattedMessage
                        id="_.rbl_drawdown.confirm.revenue_sweep"
                        defaultMessage="You'll repay this with {sweepPercentage} your of revenue each month."
                        values={{
                            sweepPercentage: (
                                <span
                                    className={styles.highlightedDrawdownValue}
                                >
                                    <VariableRate
                                        value={sweep || sweepFraction}
                                        maximumFractionDigits={2}
                                        minimumFractionDigits={0}
                                    />
                                </span>
                            )
                        }}
                    />
                </div>

                <div data-testid="rbl-drawdown-repayment-dates">
                    {firstRepaymentDate && secondRepaymentDate && (
                        <FormattedMessage
                            id="_.rbl_drawdown.confirm.repayment_dates"
                            defaultMessage="We'll take your first payment on {firstRepaymentDate}, then the {repaymentDayOfMonth} of each month."
                            values={{
                                firstRepaymentDate: (
                                    <span
                                        className={
                                            styles.highlightedDrawdownValue
                                        }
                                    >
                                        <FormattedDate
                                            value={new Date(firstRepaymentDate)}
                                            day="numeric"
                                            month="long"
                                            year="numeric"
                                        />
                                    </span>
                                ),
                                repaymentDayOfMonth: (
                                    <span
                                        className={
                                            styles.highlightedDrawdownValue
                                        }
                                    >
                                        {format(
                                            new Date(secondRepaymentDate),
                                            'do'
                                        )}
                                    </span>
                                )
                            }}
                        />
                    )}
                </div>
            </div>
        </PairItemContainer>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(ConfirmFundingInfo);
